import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { useCart } from "../context/CartContext";
import { Reveal, MaskedLines } from "../components/Motion";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import { getProducts, createCheckout } from "../lib/api";

const Shop = () => {
  const { t, lang } = useLang();
  const { items, add, remove, total, count, clear } = useCart();
  const [products, setProducts] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
  }, []);

  const handleAdd = (p) => {
    add(p);
    toast.success(t.shop.added, { description: p.title[lang] });
  };

  const checkout = async () => {
    if (!items.length || checkingOut) return;
    setCheckingOut(true);
    try {
      const res = await createCheckout({
        items: items.map((it) => ({ lookup_key: it.slug, quantity: it.qty })),
        origin_url: window.location.origin,
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch {
      toast.error(t.shop.checkoutError);
      setCheckingOut(false);
    }
  };

  const productSchema = products.length
    ? [
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: products.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: p.title[lang],
              description: p.description[lang],
              image: p.cover_image,
              brand: { "@type": "Brand", name: "MYSYMP AG" },
              offers: {
                "@type": "Offer",
                price: p.price,
                priceCurrency: p.currency,
                availability: "https://schema.org/InStock",
              },
            },
          })),
        },
      ]
    : [];

  return (
    <div data-testid="shop-page" className="bg-obsidian">
      <Seo pageKey="shop" schema={productSchema} />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-16 md:px-12 md:pt-52">
        <div className="flex items-end justify-between gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.shop.overline}</span>
            <h1 className="mt-6 font-serif text-6xl font-light leading-[0.95] tracking-tight text-white md:text-8xl">
              <MaskedLines lines={[t.shop.title]} startDelay={0.2} />
            </h1>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            data-testid="shop-cart-btn"
            className="relative flex shrink-0 items-center gap-3 border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-champagne hover:text-champagne"
          >
            <ShoppingBag size={16} /> {t.shop.cart} ({count})
          </button>
        </div>
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">{t.shop.sub}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-40 md:px-12">
        <div className="grid gap-x-8 gap-y-16 border-t border-white/10 pt-16 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.08}>
              <div className="group flex h-full flex-col" data-testid={`product-${p.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-obsidian-soft">
                  <img src={p.cover_image} alt={p.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </div>
                <span className="mt-5 text-[10px] uppercase tracking-[0.25em] text-champagne">{p.pages} {t.shop.pages}</span>
                <h3 className="mt-2 font-serif text-2xl font-light leading-snug text-white">{p.title[lang]}</h3>
                <p className="mt-1 text-sm font-light text-white/45">{p.subtitle[lang]}</p>
                <p className="mt-4 text-sm font-light leading-relaxed text-white/55 flex-1">{p.description[lang]}</p>
                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
                  <span className="font-serif text-2xl text-white">{p.currency} {p.price.toFixed(0)}</span>
                  <button
                    onClick={() => handleAdd(p)}
                    data-testid={`add-to-cart-${p.slug}`}
                    className="group/btn flex items-center gap-2 bg-champagne px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
                  >
                    <Plus size={13} /> {t.shop.add}
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FaqSection pageKey="shop" />

      {/* Cart slide-over */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setCartOpen(false)}
              className="fixed inset-0 z-[80] bg-obsidian/70 backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-[90] flex w-full max-w-md flex-col border-l border-white/10 bg-obsidian-soft"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-8 py-6">
                <h3 className="font-serif text-2xl font-light text-white">{t.shop.cart}</h3>
                <button onClick={() => setCartOpen(false)} data-testid="cart-close" className="text-white/60 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-8 py-6">
                {items.length === 0 ? (
                  <p className="mt-10 text-center text-sm font-light text-white/40">{t.shop.empty}</p>
                ) : (
                  <ul className="space-y-6">
                    {items.map((it) => (
                      <li key={it.slug} className="flex gap-4 border-b border-white/10 pb-6" data-testid={`cart-item-${it.slug}`}>
                        <img src={it.cover_image} alt={it.title[lang]} className="h-24 w-16 object-cover" />
                        <div className="flex flex-1 flex-col">
                          <h4 className="font-serif text-lg font-light text-white">{it.title[lang]}</h4>
                          <span className="text-xs text-white/40">× {it.qty}</span>
                          <span className="mt-auto text-sm text-champagne">{it.currency} {(it.price * it.qty).toFixed(0)}</span>
                        </div>
                        <button onClick={() => remove(it.slug)} data-testid={`cart-remove-${it.slug}`} className="self-start text-white/40 hover:text-champagne transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {items.length > 0 && (
                <div className="border-t border-white/10 px-8 py-6">
                  <div className="mb-5 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/50">{t.shop.total}</span>
                    <span className="font-serif text-3xl text-white">CHF {total.toFixed(0)}</span>
                  </div>
                  <button
                    onClick={checkout}
                    disabled={checkingOut}
                    data-testid="checkout-btn"
                    className="w-full bg-champagne py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light disabled:opacity-60"
                  >
                    {checkingOut ? t.shop.processing : t.shop.checkout}
                  </button>
                  <p className="mt-4 text-center text-[11px] leading-relaxed text-white/35">{t.shop.secure}</p>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Shop;
